import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import LocalizedText from '@LocalizedText';
import LocalizedImage from '@LocalizedImage';
import { smoothScrolling } from '@app/utils/utils';
import {
  HELP_SECTION_ART_SIZE_POSITION,
  HELP_TOPIC_ART_SIZE_POSITION,
  HELP_TOPIC_ART_SIZE_POSITION_STB_LINES,
  HELP_TOPIC_SHAPE_CORNERS_SIZE,
  HELP_ART_SIZE_LABELS,
  HELP_TOPIC_SHAPE_AND_SIZE,
  HELP_TOPIC_POSITION_AND_ROTATION,
  HELP_TOPIC_CONTOUR_AND_BACKGROUND,
} from '@app/constants';
import { setLabelTypePart } from '@app/utils/help';
import SafetyTrimAndBleed from './SafetyTrimAndBleed';

const ArtworkBackgroundSection = ({ labelTypePart, isCustomShapeSticker }) => {
  return (
    <div className="section-topic">
      <LocalizedText id={`qau-help-topic-${labelTypePart}-artwork-background`} tagName="h3" />
      <div>
        <LocalizedText id={`qau-help-topic-${labelTypePart}-artwork-background-p1`} tagName="p" />
        <ul>
          <LocalizedText id={`qau-help-topic-${labelTypePart}-artwork-background-l1`} tagName="li" />
          <LocalizedText id={`qau-help-topic-${labelTypePart}-artwork-background-l2`} tagName="li" />
        </ul>
        {isCustomShapeSticker && <LocalizedText id={`qau-help-topic-${labelTypePart}-artwork-background-p2`} tagName="p" />}
      </div>
    </div>
  );
};

const ApplyChangesSection = ({ isShortWePrintFlow }) => {
  return (
    <div className="section-topic">
      <LocalizedText id="qau-help-topic-sticker-design-apply-changes" tagName="h3" />
      <div>
        <LocalizedText id="qau-help-topic-sticker-design-apply-changes-p1" tagName="p" />
        <ul>
          <LocalizedText id="qau-help-topic-sticker-design-apply-changes-l1" tagName="li" />
          <LocalizedText id="qau-help-topic-sticker-design-apply-changes-l2" tagName="li" />
          {!isShortWePrintFlow && (
            <>
              <LocalizedText id="qau-help-topic-sticker-design-apply-changes-l3" tagName="li" />
              <LocalizedText id="qau-help-topic-sticker-design-apply-changes-l4" tagName="li" />
            </>
          )}
        </ul>
        <LocalizedText id="qau-help-topic-sticker-design-apply-changes-p2" tagName="p" />
      </div>
    </div>
  );
};

const ArtSizePositionSection = ({
  activeTopic,
  scrollToTopic,
  isKnownShapeSticker,
  isCustomShapeSticker,
  isStickerFlow,
  isSheetFlow,
  isDesignIn,
  isRoll,
  isShortWePrintFlow,
}) => {
  const labelTypePart = setLabelTypePart(HELP_ART_SIZE_LABELS, [isCustomShapeSticker, isKnownShapeSticker, isSheetFlow, isDesignIn]);

  useEffect(() => {
    activeTopic && scrollToTopic(activeTopic);
  }, [activeTopic, scrollToTopic]);

  useEffect(() => {
    const anchors = document.querySelectorAll(`a[href^="#"]`);
    anchors?.forEach(anchor => anchor.addEventListener('click', smoothScrolling));
    return () => anchors?.forEach(anchor => anchor.removeEventListener('click', smoothScrolling));
  }, []);

  return (
    <div id={HELP_SECTION_ART_SIZE_POSITION} className="help-section">
      <div id={HELP_TOPIC_ART_SIZE_POSITION} className="section-topic">
        <LocalizedText id={`qau-help-topic-${isStickerFlow ? 'sticker-design' : labelTypePart}`} tagName="h2" />
        {!isStickerFlow && !isDesignIn && (
          <>
            <LocalizedText id={`qau-help-topic-${labelTypePart}-topic-desc-p1`} tagName="p" />
            <LocalizedText id={`qau-help-topic-${labelTypePart}-topic-desc-p2`} tagName="p" />
          </>
        )}
        {isSheetFlow && <LocalizedText id={`qau-help-topic-${labelTypePart}-topic-desc-p3`} tagName="p" />}
        {isRoll && <LocalizedText id={`qau-help-topic-${labelTypePart}-transform-art`} tagName="h3" />}
        {(isDesignIn || isRoll) && (
          <div>
            <LocalizedText
              id={`qau-help-topic-${labelTypePart}-transform-art-p1`}
              values={{ stbLinesTopicKey: HELP_TOPIC_ART_SIZE_POSITION_STB_LINES }}
              tagName="p"
            />
            <LocalizedText id={`qau-help-topic-${labelTypePart}-transform-art-p2`} tagName="p" />
            <LocalizedText id={`qau-help-topic-${labelTypePart}-transform-art-p3`} tagName="p" />
            <LocalizedText id={`qau-help-topic-${labelTypePart}-transform-art-p4`} tagName="p" />
            <LocalizedText id={`qau-help-topic-${labelTypePart}-transform-art-p5`} tagName="p" />
          </div>
        )}
      </div>
      {isSheetFlow && (
        <>
          <div className="section-topic">
            <LocalizedText id={`qau-help-topic-zoom-${labelTypePart}`} tagName="h3" />
            <div>
              <LocalizedText id={`qau-help-topic-zoom-${labelTypePart}-p1`} tagName="p" />
              <LocalizedText id={`qau-help-topic-zoom-${labelTypePart}-p2`} tagName="p" />
              <LocalizedText
                id={`qau-help-topic-zoom-${labelTypePart}-p3`}
                tagName="p"
                values={{ stbLinesTopicKey: HELP_TOPIC_ART_SIZE_POSITION_STB_LINES }}
              />
              <LocalizedText id={`qau-help-topic-zoom-${labelTypePart}-p4`} tagName="p" />
              <LocalizedText id={`qau-help-topic-zoom-${labelTypePart}-p5`} tagName="p" />
              <LocalizedText id={`qau-help-topic-zoom-${labelTypePart}-p6`} tagName="p" />
            </div>
          </div>
          <div className="section-topic" id={HELP_TOPIC_SHAPE_CORNERS_SIZE}>
            <LocalizedText id={`qau-help-topic-shape-corners-size-${labelTypePart}`} tagName="h3" />
            <div>
              <LocalizedText id={`qau-help-topic-shape-corners-size-${labelTypePart}-p1`} tagName="p" />
              <LocalizedText id={`qau-help-topic-shape-corners-size-${labelTypePart}-p2`} tagName="p" />
              <LocalizedText id={`qau-help-topic-shape-corners-size-${labelTypePart}-p3`} tagName="p" />
              <LocalizedText id={`qau-help-topic-shape-corners-size-${labelTypePart}-p4`} tagName="p" />
              <LocalizedText id={`qau-help-topic-shape-corners-size-${labelTypePart}-p5`} tagName="p" />
            </div>
          </div>
        </>
      )}
      {!isStickerFlow && !isSheetFlow && (
        <>
          <div className="section-topic">
            <LocalizedText id={`qau-help-topic-${labelTypePart}-move-art`} tagName="h3" />
            <div>
              <LocalizedText id={`qau-help-topic-${labelTypePart}-move-art-p1`} tagName="p" />
              <LocalizedText id={`qau-help-topic-${labelTypePart}-move-art-p2`} tagName="p" />
            </div>
          </div>
          <div className="section-topic">
            <LocalizedText id={`qau-help-topic-${labelTypePart}-scale-art`} tagName="h3" />
            <div>
              <LocalizedText id={`qau-help-topic-${labelTypePart}-scale-art-p1`} tagName="p" />
              <LocalizedText id={`qau-help-topic-${labelTypePart}-scale-art-p2`} tagName="p" />
            </div>
          </div>
        </>
      )}
      {isStickerFlow && (
        <>
          <div id={HELP_TOPIC_SHAPE_AND_SIZE} className="section-topic">
            <LocalizedText id="qau-help-topic-shape-and-size" tagName="h3" />
            <div>
              <LocalizedText id={`qau-help-topic-${labelTypePart}-shape-and-size-p1`} tagName="p" />
              <div className="image-block image-art-position">
                <LocalizedImage
                  name={`sticker-shape-size-${isCustomShapeSticker ? 'custom' : 'known'}-shape.png`}
                  alt="Sticker Shape & Size"
                  allowDefault={true}
                />
              </div>
            </div>
          </div>
          <div className="section-topic">
            <LocalizedText id={`qau-help-topic-${labelTypePart}-sticker-size`} tagName="h3" />
            <div>
              <LocalizedText id={`qau-help-topic-${labelTypePart}-sticker-size-p1`} tagName="p" />
              <LocalizedText id={`qau-help-topic-${labelTypePart}-sticker-size-p2`} tagName="p" />
              {isCustomShapeSticker && <LocalizedText id={`qau-help-topic-${labelTypePart}-sticker-size-p3`} tagName="p" />}
            </div>
          </div>
        </>
      )}
      {isCustomShapeSticker && <ApplyChangesSection isShortWePrintFlow={isShortWePrintFlow} />}
      {isKnownShapeSticker && (
        <>
          <ArtworkBackgroundSection labelTypePart={labelTypePart} isCustomShapeSticker={isCustomShapeSticker} />
          <div id={HELP_TOPIC_POSITION_AND_ROTATION} className="section-topic">
            <LocalizedText id="qau-help-topic-position-and-rotation" tagName="h3" />
            <div>
              <LocalizedText id="qau-help-topic-position-and-rotation-p1" tagName="p" />
              <LocalizedText id="qau-help-topic-position-and-rotation-p2" tagName="p" />
              <LocalizedText id="qau-help-topic-position-and-rotation-p3" tagName="p" />
              <LocalizedText id="qau-help-topic-position-and-rotation-p4" tagName="p" />
            </div>
          </div>
          <div className="section-topic">
            <LocalizedText id="qau-help-topic-art-size-and-position" tagName="h3" />
            <div>
              <LocalizedText id="qau-help-topic-art-size-and-position-p1" tagName="p" />
              <LocalizedText id="qau-help-topic-art-size-and-position-p2" tagName="p" />
              <LocalizedText id="qau-help-topic-art-size-and-position-p3" tagName="p" />
              <ul>
                <LocalizedText id="qau-help-topic-art-size-and-position-l1" tagName="li" />
                <LocalizedText id="qau-help-topic-art-size-and-position-l2" tagName="li" />
              </ul>
              <LocalizedText id="qau-help-topic-art-size-and-position-p4" tagName="p" />
              <LocalizedText id="qau-help-topic-art-size-and-position-p5" tagName="p" />
              <LocalizedText id="qau-help-topic-art-size-and-position-p6" tagName="p" />
            </div>
          </div>
          <ApplyChangesSection isShortWePrintFlow={isShortWePrintFlow} />
          <SafetyTrimAndBleed
            activeTopic={activeTopic}
            scrollToTopic={scrollToTopic}
            isKnownShapeSticker={isKnownShapeSticker}
            isCustomShapeSticker={isCustomShapeSticker}
            isSheetFlow={isSheetFlow}
            isDesignIn={isDesignIn}
          />
        </>
      )}
      {!isDesignIn && !isKnownShapeSticker && (
        <SafetyTrimAndBleed
          activeTopic={activeTopic}
          scrollToTopic={scrollToTopic}
          isKnownShapeSticker={isKnownShapeSticker}
          isCustomShapeSticker={isCustomShapeSticker}
          isSheetFlow={isSheetFlow}
          isDesignIn={isDesignIn}
        />
      )}
      {isCustomShapeSticker && (
        <>
          <div id={HELP_TOPIC_CONTOUR_AND_BACKGROUND} className="section-topic">
            <LocalizedText id="qau-help-topic-contour-and-background" tagName="h3" />
            <LocalizedText id="qau-help-topic-contour-cut" tagName="h3" />
            <div>
              <LocalizedText id="qau-help-topic-contour-cut-p1" tagName="p" />
              <div className="image-block image-art-position">
                <LocalizedImage
                  name="contour-background-custom-shape.png"
                  alt="Custom Shape Sticker Contour Cut"
                  allowDefault={true}
                />
              </div>
            </div>
          </div>
          <ArtworkBackgroundSection labelTypePart={labelTypePart} isCustomShapeSticker={isCustomShapeSticker} />
        </>
      )}
    </div>
  );
};

ArtworkBackgroundSection.propTypes = {
  labelTypePart: PropTypes.string.isRequired,
  isCustomShapeSticker: PropTypes.bool.isRequired,
};

ApplyChangesSection.propTypes = {
  isShortWePrintFlow: PropTypes.bool.isRequired,
};

ArtSizePositionSection.propTypes = {
  scrollToTopic: PropTypes.func.isRequired,
  activeTopic: PropTypes.string,
  isKnownShapeSticker: PropTypes.bool.isRequired,
  isCustomShapeSticker: PropTypes.bool.isRequired,
  isSheetFlow: PropTypes.bool.isRequired,
  isStickerFlow: PropTypes.bool.isRequired,
  isDesignIn: PropTypes.bool.isRequired,
  isRoll: PropTypes.bool.isRequired,
  isShortWePrintFlow: PropTypes.bool.isRequired,
};

export default ArtSizePositionSection;
